import React, { useEffect, useState } from "react";
import { BILLING_PLANS } from "../stripeConstants";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  fetchBillingStatus,
} from "../stripeApi";
import BillingStatusCard from "../components/BillingStatusCard";
import ManageBillingButton from "../components/ManageBillingButton";
import PlanCard from "../components/PlanCard";

/*
========================================================
BILLING PAGE
--------------------------------------------------------
Purpose:
- Main tenant-facing billing page
- Shows current billing status
- Lets tenant choose a plan
- Lets tenant open customer portal
========================================================
*/

export default function BillingPage() {
  const [status, setStatus] = useState(null);
  const [busyPlan, setBusyPlan] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const [err, setErr] = useState("");

  async function loadStatus() {
    setErr("");
    try {
      const data = await fetchBillingStatus();
      setStatus(data);
    } catch (e) {
      setErr(e.message || "Failed to load billing status");
    }
  }

  useEffect(() => {
    loadStatus();
  }, []);

  async function handleChoosePlan(plan) {
    setBusyPlan(plan);
    setErr("");

    try {
      const alreadySubscribedStatuses = [
        "active",
        "trialing",
        "canceled_pending_end",
      ];

      const hasStripeBillingRecord = !!(
        status?.stripe_customer_id && status?.stripe_subscription_id
      );

      if (
        alreadySubscribedStatuses.includes(status?.subscription_status) &&
        hasStripeBillingRecord
      ) {
        const data = await createCustomerPortalSession();
        if (data?.portal_url) {
          window.location.href = data.portal_url;
          return;
        }
        throw new Error("No portal URL returned");
      }

      const data = await createCheckoutSession(plan);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      setErr(e.message || "Failed to start checkout");
    } finally {
      setBusyPlan("");
    }
  }

  async function handleManageBilling() {
    setPortalBusy(true);
    setErr("");

    try {
      const data = await createCustomerPortalSession();
      if (data?.portal_url) {
        window.location.href = data.portal_url;
        return;
      }
      throw new Error("No portal URL returned");
    } catch (e) {
      setErr(e.message || "Failed to open billing portal");
    } finally {
      setPortalBusy(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 8px" }}>Billing</h1>
        <div style={{ color: "#9fb0c3" }}>
          Manage your JobTelesis subscription and billing settings.
        </div>
      </div>

      {err && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(255,0,0,0.08)",
            border: "1px solid rgba(255,0,0,0.18)",
            color: "#ffb3b3",
          }}
        >
          {err}
        </div>
      )}

      <BillingStatusCard status={status} />

      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          marginBottom: 20,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Manage Subscription</h2>
        <div style={{ color: "#9fb0c3", marginBottom: 16 }}>
          Open billing portal to manage payment methods and subscription details.
        </div>

        <ManageBillingButton onClick={handleManageBilling} busy={portalBusy} />
      </div>

      <div
        style={{
          background: "#13293d",
          borderRadius: 12,
          padding: 20,
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Choose a Plan</h2>
        <div style={{ color: "#9fb0c3", marginBottom: 16 }}>
          Select the subscription plan that fits your workspace.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 16,
          }}
        >
          {BILLING_PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              busy={busyPlan === plan.key}
              onSelect={handleChoosePlan}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
